import config from '@/Constants';
import { useAuth } from '@/hooks/Authentication/auth';
import { QueryResponse } from '@/modules/queryfilter/domain/interfaces';
import { Favorite } from '@/modules/shared/domain/interfaces';
import { AxiosHelper } from '@/modules/shared/infra/protocols/http';
import snakeToCamel from '@/utils/snakeToCamel';
import React from 'react';
import { QueryFunctionContext, QueryKey, useInfiniteQuery, useQueryClient } from 'react-query';

type PaginatedFavoritesQuery = QueryResponse<Favorite> & {
  nextPage: number | undefined;
};

export const favoritesKey = config.FAVORITES_URL;

const getPageParams = (page: number, size = 30) => `?size=${size}&page=${page}`;

const fetchFavorites = async (
  params: QueryFunctionContext<QueryKey, any>,
): Promise<PaginatedFavoritesQuery> => {
  const { queryKey, pageParam = 1 } = params;
  const [url, pageSize, queryString] = queryKey;
  const pageParams = getPageParams(pageParam, pageSize as number);
  
  const response = await AxiosHelper.get({
    url: `${url}${pageParams}${queryString}`,
    apiV: 1,
  });

  if (response.statusCode < 200 || response.statusCode > 300) {
    throw new Error('Unexpected error fetching favorites');
  }

  const body: PaginatedFavoritesQuery = response?.body;

  return {
    ...body,
    nextPage: body?.hasNextPage ? Number(pageParam) + 1 : undefined,
  };
};

export function useFavoritesQuery(
  params: { favoriteType?: string; screen?: string; pageSize?: number } = {},
) {
  const { user } = useAuth();
  const { pageSize = 30, favoriteType, screen } = params;
  
  const queryString = React.useMemo(() => {
    let query = '';
    if (favoriteType) query += `&favorite_type=${favoriteType}`;
    if (screen) query += `&screen=${screen}`;
    return query;
  }, [favoriteType, screen]);

  const memoizedQueryKey = React.useMemo(
    () => [favoritesKey, pageSize, queryString, user?.id],
    [pageSize, queryString, user?.id],
  );

  const query = useInfiniteQuery<PaginatedFavoritesQuery>(
    memoizedQueryKey,
    fetchFavorites,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => lastPage.nextPage,
    },
  );

  const queryClient = useQueryClient();
  const { data, isLoading, fetchNextPage, hasNextPage, refetch } = query;

  const favorites = React.useMemo(() => {
    return data?.pages.flatMap((page) => page.items) || [];
  }, [data]);

  const deleteFavorite = React.useCallback(
    async (id: string) => {
      const response = await AxiosHelper.delete({
        url: `${favoritesKey}/${id}`,
        apiV: 1,
      });

      if (response.statusCode >= 200 && response.statusCode < 300) {
        queryClient.invalidateQueries(favoritesKey);
      } else {
        throw new Error('Unexpected error deleting favorite');
      }
    },
    [queryClient],
  );

  return {
    isLoading,
    favorites,
    fetchNextPage,
    hasNextPage,
    refetch,
    deleteFavorite,
  };
}
